// Payment methods: types, a `use…()` hook and a picker every module can drop into a form.
//
// Typed into a text box, "card", "Card" and "credit card" are three methods; picked from the
// list they are one, and the one that settles against the right account. The list is tenant-wide,
// so the picker needs no company and the same control serves a customer card and a sales invoice.
import { useApi } from "@keel/core";
import { Select } from "../Select";
import { Field } from "../ui";

export type PaymentMethodKind = "bank_transfer" | "card" | "cash" | "direct_debit" | "cheque" | "other";

export interface PaymentMethod {
  id: string;
  /** Upper-case; what cards and documents store. */
  code: string;
  name: string;
  kind: PaymentMethodKind;
  /** G/L account a document paid this way is settled against when it posts; null keeps it open. */
  balancingAccountCode: string | null;
  /** A document paid this way has to carry a reference — a mandate, a terminal slip, a cheque number. */
  requiresReference: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** The kinds in the order a form offers them, with the label a person reads. */
export const PAYMENT_METHOD_KINDS: [PaymentMethodKind, string][] = [
  ["bank_transfer", "Bank transfer"],
  ["card", "Card"],
  ["cash", "Cash"],
  ["direct_debit", "Direct debit"],
  ["cheque", "Cheque"],
  ["other", "Other"],
];

export const paymentMethodKindLabel = (kind: string) =>
  PAYMENT_METHOD_KINDS.find(([v]) => v === kind)?.[1] ?? kind;

/** Active methods by default; the maintenance table asks for the retired ones too. */
export function usePaymentMethods(opts: { includeInactive?: boolean } = {}) {
  return useApi<PaymentMethod[]>(
    opts.includeInactive ? "/setup/payment-methods?includeInactive=true" : "/setup/payment-methods",
  );
}

export interface PaymentMethodSelectProps {
  value: string | null | undefined;
  onChange: (code: string) => void;
  /** Allow no method at all, with this label. */
  emptyLabel?: string;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * The bare control. A retired method that is already on the record stays selectable, so opening an
 * old document does not silently blank the field; it is marked so nobody picks it on purpose.
 */
export function PaymentMethodSelect({
  value,
  onChange,
  emptyLabel,
  disabled,
  placeholder = "Choose a payment method…",
}: PaymentMethodSelectProps) {
  const methods = usePaymentMethods({ includeInactive: true }).data ?? [];
  const offered = methods.filter((m) => m.isActive || m.code === value);
  return (
    <Select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
    >
      {emptyLabel && <option value="">{emptyLabel}</option>}
      {offered.map((m) => (
        <option key={m.id} value={m.code} disabled={!m.isActive}>
          {m.name} · {m.code}
          {m.isActive ? "" : " (retired)"}
        </option>
      ))}
    </Select>
  );
}

/** The same control with its label, and a hint that says what the chosen method will do on posting. */
export function PaymentMethodField({
  label,
  hint,
  ...rest
}: PaymentMethodSelectProps & { label: string; hint?: string }) {
  const chosen = usePaymentMethods({ includeInactive: true }).data?.find((m) => m.code === rest.value);
  const effect = chosen?.balancingAccountCode
    ? `Settled against ${chosen.balancingAccountCode} when the document posts`
    : chosen?.requiresReference
      ? "A payment reference is required"
      : hint;
  return (
    <Field label={label} hint={effect}>
      <PaymentMethodSelect {...rest} />
    </Field>
  );
}
